import { ComponentProps, useMemo, useRef, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import { MultiSelect } from "@bonliva-crm/ui";
import locale from "@bonliva-crm/locale";
import { useGlobalStore, useUpdateEffect } from "@hooks";
import { FilterWithFavouriteProps } from "../utils/filters-record-types";
import { ControllableCollapsibleFavFilter } from "./_base/controllable-collapsible-fav-filter";
import { isEmpty } from "lodash";

type Option = { label: string; value: string | null };

export type WorksViaCompetitorsFilterProps = {
  label?: string;
  options?: Option[];
} & FilterWithFavouriteProps;

export const WorksViaCompetitorsFilter = ({
  label,
  options,
  ...props
}: WorksViaCompetitorsFilterProps) => {
  const store = useGlobalStore();
  const title = label ?? locale["Works via competitors"];
  const _options = useMemo(() => {
    return (
      options ?? [
        { label: "(Har konkurrent agiven)", value: "(any)" },
        ...store.generalSearchStore.allCompetitors.toLabelValue()
      ]
    );
  }, [store.generalSearchStore.allCompetitors]);

  const selectRef = useRef(null);
  const { setValue, resetField } = useFormContext();
  const { field, fieldState } = useController({ name: props.name });
  const [expanded, setExpanded] = useState(fieldState.isDirty);

  useUpdateEffect(() => {
    if (expanded) {
      selectRef.current.focus();
    }
  }, [expanded]);

  const clear = () => resetField(props.name);

  const badgeContent = !isEmpty(field.value)
    ? _options.find((o) => o.value === field.value[0])?.label
    : null;

  const commonProps: ComponentProps<typeof MultiSelect<Option>> = {
    options: _options,
    name: props.name,
    value: field?.value?.map((v: string) => _options.find((x) => v === x.value)) ?? [],
    onChange: (selected) => {
      setValue(
        props.name,
        selected.map((s) => s.value),
        {
          shouldDirty: true
        }
      );
    },
    hideSelectedOptions: false,
    controlShouldRenderValue: false,
    placeholder: title,
    isSearchable: true,
    maxMenuHeight: 300,
    showBadge: field?.value !== null,
    badgeContent,
    optionType: "checkbox",
    isClearable: field?.value !== null,
    onClear: clear,
    closeMenuOnSelect: false,
    hidePlaceholderOnFocus: false,
    showOptionDivider: false
  };

  if (props.setFavorite) {
    return (
      <ControllableCollapsibleFavFilter
        title={title}
        isDirty={fieldState.isDirty}
        isClearable={fieldState.isDirty}
        isExpanded={expanded}
        onClear={clear}
        favorite={props.favorite}
        onSetFavorite={props.setFavorite}
        onExpand={() => setExpanded(!expanded)}
      >
        <MultiSelect<Option>
          {...commonProps}
          selectRef={selectRef}
          isClearable={false}
          openMenuOnFocus
          menuPlacement="auto"
          menuPortalTarget={document.body}
          controlShouldRenderValue
          showBadge={false}
          placeholder={locale.SelectValue}
          styleProps={{
            menu: { showBorder: true },
            valueContainer: { padding: 0 },
            menuPortal: { zIndex: 9999 }
          }}
        />
      </ControllableCollapsibleFavFilter>
    );
  }

  return (
    <MultiSelect<Option>
      {...commonProps}
      styleProps={{
        control: { showBorder: true },
        menu: { showBorder: true }
      }}
    />
  );
};
